import apiClient from '../lib/api/client';
import { getApiUrl } from '../config/config';
import { prepareAttachmentForUpload } from '../components/chat/utils/fileUtils';

// Get all chats for the "All" tab
// params: { cursor, limit, search } - cursor-based pagination (undefined keys are dropped by axios)
export const getAllChats = async (params = {}) => {
  try {
    const response = await apiClient.get(getApiUrl('CHAT', 'GET_ALL_CHATS'), { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching all chats:', error);
    throw error;
  }
};

// Get alerts chats for the "Alerts" tab
// params: { cursor, limit, search } - cursor-based pagination
export const getAlertsChats = async (params = {}) => {
  try {
    const response = await apiClient.get(getApiUrl('CHAT', 'GET_ALERTS_CHATS'), { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching alerts chats:', error);
    throw error;
  }
};

// Get My chats for the "My" tab
// params: { cursor, limit, search } - cursor-based pagination
export const getMyChats = async (params = {}) => {
  try {
    const response = await apiClient.get(getApiUrl('CHAT', 'GET_MY_CHATS'), { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching my chats:', error);
    throw error;
  }
};

// Get Inbox chats for the "Inbox" tab
// params: { cursor, limit, search } - cursor-based pagination
export const getInboxChats = async (params = {}) => {
  try {
    const response = await apiClient.get(getApiUrl('CHAT', 'GET_INBOX_CHATS'), { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching inbox chats:', error);
    throw error;
  }
};

// Get Pinned chats for the "Pinned" tab
export const getPinChats = async () => {
  try {
    const response = await apiClient.get(getApiUrl('CHAT', 'GET_PIN_CHATS'));
    return response.data;
  } catch (error) {
    console.error('Error fetching pin chats:', error);
    throw error;
  }
};

// Conversation APIs
export const getConversations = async (filters = {}) => {
  try {
    const response = await apiClient.get(getApiUrl('CHAT', 'GET_CONVERSATIONS'), { params: filters });
    return response.data;
  } catch (error) {
    console.error('Error fetching conversations:', error);
    throw error;
  }
};

export const getConversation = async (conversationId) => {
  try {
    const response = await apiClient.get(`${getApiUrl('CHAT', 'GET_CONVERSATION')}/${conversationId}/`);
    return response.data;
  } catch (error) {
    console.error('Error fetching conversation:', error);
    throw error;
  }
};

// Create a new conversation
export const createConversation = async (contactData) => {
  try {
    console.log('📞 Creating new conversation with contact:', contactData);
    
    // Get phone number (ensure it's digits only, no + prefix)
    const phoneNumber = contactData.wa_phone || contactData.phone || '';
    const phoneWithoutPrefix = phoneNumber.startsWith('+') 
      ? phoneNumber.substring(1) 
      : phoneNumber;
    
    // Prepare the request payload - try different possible formats
    // Format 1: With primary_contact object
    const payload = {
      primary_contact: {
        wa_phone: phoneWithoutPrefix,
        name: contactData.name || '',
        email: contactData.email || ''
      },
      channel: 'whatsapp',
      subject: contactData.name ? `Chat with ${contactData.name}` : `Chat with ${phoneWithoutPrefix}`
    };

    // Try POST to /api/chats/conversations/
    let response;
    try {
      response = await apiClient.post('/api/chats/conversations/', payload, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
    } catch (firstError) {
      // If that fails, try alternative format with just phone number
      console.log('⚠️ First format failed, trying alternative format...');
      const altPayload = {
        wa_phone: phoneWithoutPrefix,
        name: contactData.name || '',
        email: contactData.email || '',
        channel: 'whatsapp'
      };
      response = await apiClient.post('/api/chats/conversations/', altPayload, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
    }
    
    console.log('✅ Conversation created successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Error creating conversation:', error);
    if (error.response) {
      console.error('❌ Response error details:', {
        status: error.response.status,
        statusText: error.response.statusText,
        data: error.response.data
      });
    }
    throw error;
  }
};

// Message APIs
export const getMessages = async (conversationId) => {
  try {
    const response = await apiClient.get(`${getApiUrl('CHAT', 'GET_MESSAGES')}/${conversationId}/messages/`);
    return response.data;
  } catch (error) {
    console.error('Error fetching messages:', error);
    throw error;
  }
};

// Get messages for a specific conversation (new API structure)
export const getConversationMessages = async (conversationId, page = 1) => {
  try {
    const url = `${getApiUrl('CHAT', 'GET_MESSAGES')}/${conversationId}/messages/?page=${page}`;
    console.log('🔗 API URL:', url);
    console.log('🔑 Auth token present:', !!localStorage.getItem('access_token'));
    console.log('📄 Loading page:', page);
    
    const response = await apiClient.get(url);
    console.log('📡 API Response status:', response.status);
    console.log('📡 API Response data type:', typeof response.data);
    console.log('📡 API Response data:', response.data);
    
    // Handle different response structures
    if (response.data && typeof response.data === 'object') {
      
      // If response has isSuccess property, it's a wrapped response
      if ('isSuccess' in response.data) {
        console.log('📦 Found isSuccess property:', response.data.isSuccess);
        if (response.data.isSuccess && Array.isArray(response.data.data)) {
          console.log('✅ API returned wrapped success response with data array');
          if (
            'only_template_allow' in response.data ||
            'next' in response.data ||
            'previous' in response.data
          ) {
            return {
              data: response.data.data,
              next: response.data.next,
              previous: response.data.previous,
              only_template_allow: response.data.only_template_allow === true
            };
          }
          return response.data.data;
        } else {
          console.warn('⚠️ API returned wrapped response but not successful or data not array:', response.data);
          throw new Error(response.data.error || 'API returned unsuccessful response');
        }
      }
      // If response.data is directly an array, return it
      else if (Array.isArray(response.data)) {
        console.log('✅ API returned direct array response');
        return response.data;
      }
      // Check if it's a different wrapped response structure
      else if ('data' in response.data && Array.isArray(response.data.data)) {
        console.log('✅ API returned wrapped response with data array (no isSuccess)');
        if (
          'only_template_allow' in response.data ||
          'next' in response.data ||
          'previous' in response.data
        ) {
          return {
            data: response.data.data,
            next: response.data.next,
            previous: response.data.previous,
            only_template_allow: response.data.only_template_allow === true
          };
        }
        return response.data.data;
      }
      // Check if it's a different structure with messages array
      else if ('messages' in response.data && Array.isArray(response.data.messages)) {
        console.log('✅ API returned response with messages array');
        console.log('📄 Pagination info:', {
          next: response.data.next,
          previous: response.data.previous,
          totalMessages: response.data.messages.length
        });
        return {
          messages: response.data.messages,
          pagination: {
            hasNext: response.data.next,
            hasPrevious: response.data.previous
          },
          only_template_allow: response.data.only_template_allow === true
        };
      }
      // Check if it's a different structure with message array (singular key)
      else if ('message' in response.data && Array.isArray(response.data.message)) {
        console.log('✅ API returned response with message array');
        console.log('📄 Pagination info:', {
          next: response.data.next,
          previous: response.data.previous,
          totalMessages: response.data.message.length
        });
        return {
          messages: response.data.message,
          pagination: {
            hasNext: response.data.next,
            hasPrevious: response.data.previous
          },
          only_template_allow: response.data.only_template_allow === true
        };
      }
      // Check if it's a different structure with results array
      else if ('results' in response.data && Array.isArray(response.data.results)) {
        console.log('✅ API returned response with results array');
        return response.data.results;
      }
      // If response.data is an object but not the expected structure
      else {
        console.warn('⚠️ API returned object but not array or recognized wrapped response:', response.data);
        console.warn('🔍 Available keys:', Object.keys(response.data));
        throw new Error('Unexpected response format: object but not array or recognized wrapped response');
      }
    }
    // If response.data is not an object
    else {
      console.warn('⚠️ API returned non-object response:', response.data);
      throw new Error('Unexpected response format: not an object');
    }
  } catch (error) {
    console.error('❌ Error fetching conversation messages:', error);
    console.error('❌ Error response:', error.response);
    throw error;
  }
};

// Accept a chat conversation
export const acceptChat = async (conversationId) => {
  try {
    const response = await apiClient.post(`${getApiUrl('CHAT', 'GET_MESSAGES')}/${conversationId}/accept/`);
    return response.data;
  } catch (error) {
    console.error('Error accepting chat:', error);
    throw error;
  }
};

// Reject a chat conversation
export const rejectChat = async (conversationId) => {
  try {
    const response = await apiClient.post(`${getApiUrl('CHAT', 'GET_MESSAGES')}/${conversationId}/reject/`);
    return response.data;
  } catch (error) {
    console.error('Error rejecting chat:', error);
    throw error;
  }
};

// Move conversation to Alerts
export const moveToAlerts = async (conversationId) => {
  try {
    const response = await apiClient.post(`${getApiUrl('CHAT', 'GET_MESSAGES')}/${conversationId}/move_to_alerts/`);
    return response.data;
  } catch (error) {
    console.error('Error moving conversation to alerts:', error);
    throw error;
  }
};

// Resolve a chat conversation
export const resolveChat = async (conversationId) => {
  try {
    const response = await apiClient.post(`${getApiUrl('CHAT', 'GET_MESSAGES')}/${conversationId}/resolve/`);
    return response.data;
  } catch (error) {
    console.error('Error resolving chat:', error);
    throw error;
  }
};

// Accept transfer for a chat conversation (for Pending Transfer status)
export const transferAccept = async (conversationId) => {
  try {
    const response = await apiClient.post(`${getApiUrl('CHAT', 'GET_MESSAGES')}/${conversationId}/transfer_accept/`);
    return response.data;
  } catch (error) {
    console.error('Error accepting transfer:', error);
    throw error;
  }
};

// Reject transfer for a chat conversation (for Pending Transfer status)
export const transferReject = async (conversationId) => {
  try {
    const response = await apiClient.post(`${getApiUrl('CHAT', 'GET_MESSAGES')}/${conversationId}/transfer_reject/`);
    return response.data;
  } catch (error) {
    console.error('Error rejecting transfer:', error);
    throw error;
  }
};

/** Accept pending invite (invited user, Inbox — Pending Invite) */
export const inviteAccept = async (conversationId) => {
  try {
    const response = await apiClient.post(
      `${getApiUrl('CHAT', 'GET_MESSAGES')}/${conversationId}/invite_accept/`
    );
    return response.data;
  } catch (error) {
    console.error('Error accepting invite:', error);
    throw error;
  }
};

/** Reject pending invite (invited user, Inbox — Pending Invite) */
export const inviteReject = async (conversationId) => {
  try {
    const response = await apiClient.post(
      `${getApiUrl('CHAT', 'GET_MESSAGES')}/${conversationId}/invite_reject/`
    );
    return response.data;
  } catch (error) {
    console.error('Error rejecting invite:', error);
    throw error;
  }
};

/** Leave conversation (invited collaborator/member) */
export const leaveConversation = async (conversationId) => {
  try {
    const response = await apiClient.post(
      `${getApiUrl('CHAT', 'GET_MESSAGES')}/${conversationId}/leave/`
    );
    return response.data;
  } catch (error) {
    console.error('Error leaving conversation:', error);
    throw error;
  }
};

// Send text-only message
export const sendTextMessage = async (conversationId, messageData) => {
  try {
    console.log('📤 Sending text message:', {
      conversationId,
      body: messageData.body
    });
    
    const response = await apiClient.post(
      `${getApiUrl('CHAT', 'GET_MESSAGES')}/${conversationId}/messages/`,
      {
        body: messageData.body,
        ...(messageData.reply_to_id != null ? { reply_to_id: messageData.reply_to_id } : {}),
        conversation: conversationId,
        sender_type: "AGENT"
      },
      {
        headers: {
          'Content-Type': 'application/json'
        }
      }
    );
    
    console.log('✅ Text message response:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error sending text message:', error);
    throw error;
  }
};

// Validate voice message format for WhatsApp
const validateVoiceMessageForWhatsApp = (file) => {
  if (!file) return { isValid: false, reason: 'No file provided' };
  
  const isOGG = file.type === 'audio/ogg; codecs=opus' || file.name.endsWith('.ogg');
  const isOPUS = file.type.includes('opus');
  const isAudio = file.type.includes('audio');
  
  return {
    isValid: isOGG && isOPUS && isAudio,
    isOGG,
    isOPUS,
    isAudio,
    reason: !isAudio ? 'Not an audio file' : 
            !isOPUS ? 'Not OPUS codec' : 
            !isOGG ? 'Not OGG format' : 
            'Valid for WhatsApp voice notes'
  };
};

// Send message with a single attachment/voice note
export const sendMediaMessage = async (conversationId, messageData) => {
  try {
    const formData = new FormData();
    const file = messageData.file ? await prepareAttachmentForUpload(messageData.file) : null;
    
    // Add file if present
    if (file) {
      // Validate voice message format for WhatsApp
      if (file.type.includes('audio') || file.name.includes('voice')) {
        const validation = validateVoiceMessageForWhatsApp(file);
        console.log('🎤 Voice message validation:', validation);
        
        if (!validation.isValid) {
          console.warn('⚠️ Voice message may not appear properly in WhatsApp:', validation.reason);
        } else {
          console.log('✅ Voice message format is valid for WhatsApp');
        }
      }
      
      formData.append('file', file);
    }
    
    // When file is attached: text goes in caption; body is empty. Text-only messages use body.
    formData.append('body', messageData.body || '');
    
    // For voice messages without user caption, use default label; otherwise use user text (caption)
    let caption = messageData.caption || '';
    if (!caption && file && (file.type.includes('audio') || file.name.includes('voice'))) {
      if (file.type === 'audio/ogg; codecs=opus' || file.name.endsWith('.ogg')) {
        caption = 'Voice Message';
      } else {
        caption = 'Audio Message';
      }
    }
    formData.append('caption', caption);

    if (messageData.reply_to_id != null) {
      formData.append('reply_to_id', String(messageData.reply_to_id));
    }
    
    // Add additional metadata for voice messages that WhatsApp might need
    if (file && (file.type.includes('audio') || file.name.includes('voice'))) {
      // Add media type indicator for WhatsApp
      formData.append('media_type', 'voice');
      
      // Add duration if available (for voice notes)
      // Format duration as MM:SS or HH:MM:SS as required by server
      if (messageData.duration) {
        const durationSeconds = typeof messageData.duration === 'number' 
          ? messageData.duration 
          : parseFloat(messageData.duration);
        
        if (!isNaN(durationSeconds) && durationSeconds >= 0) {
          const hours = Math.floor(durationSeconds / 3600);
          const minutes = Math.floor((durationSeconds % 3600) / 60);
          const seconds = Math.floor(durationSeconds % 60);
          
          // Format as HH:MM:SS if >= 1 hour, otherwise MM:SS
          const formattedDuration = hours > 0
            ? `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
            : `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
          
          formData.append('duration', formattedDuration);
        }
      }
      
      // Add voice note specific metadata
      if (file.type === 'audio/ogg; codecs=opus' || file.name.endsWith('.ogg')) {
        formData.append('is_voice_note', 'true');
        formData.append('audio_format', 'ogg_opus');
      } else {
        formData.append('is_voice_note', 'false');
        formData.append('audio_format', 'generic');
      }
    }
    
    formData.append('conversation', conversationId.toString());
    formData.append('sender_type', 'AGENT');
    
    console.log('📤 Sending media message:', {
      conversationId,
      file: file?.name || 'No file',
      fileType: file?.type || 'No type',
      fileSize: file?.size || 'No size',
      isFileObject: file instanceof File,
      body: messageData.body,
      caption: messageData.caption,
      isVoiceNote: file?.type === 'audio/ogg; codecs=opus' || file?.name?.endsWith('.ogg'),
      isOPUS: file?.type?.includes('opus')
    });
    
    // Debug FormData contents
    console.log('📋 FormData contents:');
    for (let [key, value] of formData.entries()) {
      console.log(`${key}:`, value);
    }
    
    const response = await apiClient.post(
      `${getApiUrl('CHAT', 'GET_MESSAGES')}/${conversationId}/messages/`,
      formData,
      {
        headers: {
          // Let axios set the Content-Type with boundary for FormData
          'Content-Type': 'multipart/form-data'
        },
        timeout: 30000, // 30 seconds timeout for file uploads
        onUploadProgress: (progressEvent) => {
          if (messageData.onUploadProgress) {
            const percentCompleted = Math.min(Math.round((progressEvent.loaded * 100) / progressEvent.total), 100);
            messageData.onUploadProgress(percentCompleted);
          }
        }
      }
    );
    
    console.log('✅ Media message response:', response.data);
    
    // Additional debugging for voice messages
    if (messageData.file && (messageData.file.type.includes('audio') || messageData.file.name.includes('voice'))) {
      console.log('🎤 Voice message response details:', {
        id: response.data.id,
        media_kind: response.data.media_kind,
        media_mime: response.data.media_mime,
        file: response.data.file,
        wa_media_id: response.data.wa_media_id,
        meta_status: response.data.meta_status,
        sender_type: response.data.sender_type,
        conversation: response.data.conversation,
        created_at: response.data.created_at
      });
      
      // Check if the response indicates successful WhatsApp processing
      if (response.data.wa_media_id) {
        console.log('✅ Voice message has WhatsApp media ID:', response.data.wa_media_id);
      } else {
        console.warn('⚠️ Voice message missing WhatsApp media ID - may not appear in WhatsApp');
        console.warn('💡 This could be due to:');
        console.warn('   - Backend not processing voice messages correctly');
        console.warn('   - WhatsApp API not accepting the voice format');
        console.warn('   - Missing required metadata for voice notes');
      }
      
      if (response.data.meta_status) {
        console.log('📊 Voice message meta status:', response.data.meta_status);
        if (response.data.meta_status !== 'sent' && response.data.meta_status !== 'delivered') {
          console.warn('⚠️ Voice message status indicates potential delivery issue:', response.data.meta_status);
        }
      }
      
      // Check media kind for voice messages
      if (response.data.media_kind) {
        console.log('🎵 Voice message media kind:', response.data.media_kind);
        if (response.data.media_kind !== 'audio' && response.data.media_kind !== 'voice') {
          console.warn('⚠️ Voice message media kind is not audio/voice:', response.data.media_kind);
        }
      }
    }
    
    return response.data;
  } catch (error) {
    console.error('❌ Error sending media message:', error);
    if (error.response) {
      console.error('❌ Response error details:', {
        status: error.response.status,
        statusText: error.response.statusText,
        data: error.response.data,
        headers: error.response.headers
      });
    }
    throw error;
  }
};

// Send multiple media messages (one API call per file)
export const sendMultipleMediaMessages = async (conversationId, messageData) => {
  try {
    const results = [];
    const totalFiles = messageData.attachments.length + (messageData.audioBlob ? 1 : 0);
    let completedFiles = 0;
    
    // Send each attachment separately
    for (let i = 0; i < messageData.attachments.length; i++) {
      const file = messageData.attachments[i];
      const mediaData = {
        file: file,
        body: messageData.body || '',
        caption: messageData.caption || '',
        ...(messageData.reply_to_id != null ? { reply_to_id: messageData.reply_to_id } : {}),
        onUploadProgress: (progress) => {
          if (messageData.onUploadProgress) {
            // Calculate overall progress including all files
            const fileProgress = (progress / totalFiles);
            const overallProgress = Math.min(Math.round(((completedFiles + fileProgress) / totalFiles) * 100), 100);
            messageData.onUploadProgress(overallProgress);
          }
        }
      };
      
      const response = await sendMediaMessage(conversationId, mediaData);
      results.push(response);
      completedFiles++;
    }
    
    // Send voice note if present
    if (messageData.audioBlob) {
      console.log('🎤 Sending voice note:', {
        name: messageData.audioBlob.name,
        type: messageData.audioBlob.type,
        size: messageData.audioBlob.size,
        isFile: messageData.audioBlob instanceof File,
        constructor: messageData.audioBlob.constructor.name
      });
      
      const mediaData = {
        file: messageData.audioBlob,
        body: messageData.body || '',
        caption: messageData.caption || '',
        duration: messageData.duration || null, // Include duration for voice messages
        ...(messageData.reply_to_id != null ? { reply_to_id: messageData.reply_to_id } : {}),
        needTranscode:!(
          messageData.audioBlob?.type === 'audio/ogg; codecs=opus' || messageData.audioBlob?.name?.endsWith('.ogg')
        ),
        onUploadProgress: (progress) => {
          if (messageData.onUploadProgress) {
            // Calculate overall progress including all files
            const fileProgress = (progress / totalFiles);
            const overallProgress = Math.min(Math.round(((completedFiles + fileProgress) / totalFiles) * 100), 100);
            messageData.onUploadProgress(overallProgress);
          }
        }
      };
      
      const response = await sendMediaMessage(conversationId, mediaData);
      results.push(response);
      completedFiles++;
    }
    
    return results;
  } catch (error) {
    console.error('Error sending multiple media messages:', error);
    throw error;
  }
};

// Main send message function that decides which API to use
export const sendMessage = async (conversationId, messageData) => {
  try {
    // Check if there are attachments or voice notes
    const hasAttachments = messageData.attachments && messageData.attachments.length > 0;
    const hasVoiceNote = messageData.audioBlob;
    
    if (hasAttachments || hasVoiceNote) {
      // Use multiple media messages API (one call per file)
      return await sendMultipleMediaMessages(conversationId, messageData);
    } else {
      // Use text-only message API
      return await sendTextMessage(conversationId, messageData);
    }
  } catch (error) {
    console.error('Error sending message:', error);
    throw error;
  }
};

// ===== Spam APIs ('Mark Chat as Spam' story) =====

// Get all spam conversations (GET /api/chats/spam-chats/)
// params: { cursor, limit, search } - cursor-based pagination
export const getSpamChats = async (params = {}) => {
  try {
    const response = await apiClient.get('/api/chats/spam-chats/', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching spam chats:', error);
    throw error;
  }
};

// Mark a conversation as spam (POST /api/chats/conversations/{id}/mark-spam/)
export const markConversationAsSpam = async (conversationId) => {
  try {
    const response = await apiClient.post(
      `/api/chats/conversations/${conversationId}/mark-spam/`
    );
    return response.data;
  } catch (error) {
    console.error('Error marking conversation as spam:', error);
    throw error;
  }
};

// Remove a conversation from spam (POST /api/chats/conversations/{id}/remove-from-spam/)
export const removeConversationFromSpam = async (conversationId) => {
  try {
    const response = await apiClient.post(
      `/api/chats/conversations/${conversationId}/remove-from-spam/`
    );
    return response.data;
  } catch (error) {
    console.error('Error removing conversation from spam:', error);
    throw error;
  }
};

// Contact APIs
export const getContacts = async () => {
  try {
    const response = await apiClient.get(getApiUrl('CHAT', 'GET_CONTACTS'));
    return response.data;
  } catch (error) {
    console.error('Error fetching contacts:', error);
    throw error;
  }
};

export const createContact = async (contactData) => {
  try {
    const response = await apiClient.post(getApiUrl('CHAT', 'CREATE_CONTACT'), contactData);
    return response.data;
  } catch (error) {
    console.error('Error creating contact:', error);
    throw error;
  }
};

// Invite user to chat (legacy global endpoint)
export const inviteUserToChat = async (chatId, userId) => {
  try {
    const response = await apiClient.post(getApiUrl('CHAT', 'INVITE_USER'), {
      chat_id: chatId,
      user_id: userId
    });
    return response.data;
  } catch (error) {
    console.error('Error inviting user to chat:', error);
    throw error;
  }
};

/** POST /api/chats/conversations/{conversationId}/invite/ — pending invite for selected agent */
export const inviteAgentToConversation = async (conversationId, userId) => {
  try {
    const response = await apiClient.post(
      `${getApiUrl('CHAT', 'GET_MESSAGES')}/${conversationId}/invite/`,
      { to_user_id: userId }
    );
    return response.data;
  } catch (error) {
    console.error('Error inviting agent to conversation:', error);
    throw error;
  }
};

/** GET /api/chats/conversations/{conversationId}/invited-agents/ */
export const getInvitedAgentsByConversation = async (conversationId) => {
  try {
    const response = await apiClient.get(
      `${getApiUrl('CHAT', 'GET_MESSAGES')}/${conversationId}/invited-agents/`
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching invited agents:', error);
    throw error;
  }
};

/** Remove invited/collaborator user from conversation */
export const removeInvitedAgentFromConversation = async (conversationId, userId) => {
  const payload = { user_id: userId };
  const base = `${getApiUrl('CHAT', 'GET_MESSAGES')}/${conversationId}`;
  try {
    // Primary endpoint as provided by backend: /api/chats/conversations/{id}//
    const response = await apiClient.post(`${base}//`, payload);
    return response.data;
  } catch (primaryError) {
    try {
      // Compatibility fallback
      const response = await apiClient.post(`${base}/remove-collaborator/`, payload);
      return response.data;
    } catch (fallbackOneError) {
      try {
        const response = await apiClient.post(`${base}/remove-collaborator/`, payload);
        return response.data;
      } catch (fallbackTwoError) {
        console.error('Error removing invited agent from conversation:', fallbackTwoError || fallbackOneError || primaryError);
        throw (fallbackTwoError || fallbackOneError || primaryError);
      }
    }
  }
};

// Assign chat to agent
export const assignChatToAgent = async (conversationId, userId) => {
  try {
    const response = await apiClient.post(`${getApiUrl('CHAT', 'GET_MESSAGES')}/${conversationId}/assign/`, {
      user_id: userId
    });
    return response.data;
  } catch (error) {
    console.error('Error assigning chat to agent:', error);
    throw error;
  }
};

// Transfer chat to another agent/admin
export const transferChat = async (conversationId, toUserId) => {
  try {
    const response = await apiClient.post(`${getApiUrl('CHAT', 'TRANSFER_CHAT')}/${conversationId}/transfer/`, {
      to_user_id: toUserId
    });
    return response.data;
  } catch (error) {
    console.error('Error transferring chat:', error);
    throw error;
  }
};

// Add user to chat
export const addUserToChat = async (conversationId, userId) => {
  try {
    const response = await apiClient.post(`${getApiUrl('CHAT', 'GET_MESSAGES')}/${conversationId}/add-user/`, {
      user_id: userId
    });
    return response.data;
  } catch (error) {
    console.error('Error adding user to chat:', error);
    throw error;
  }
};

// Get My chats by user ID for filtering
// params: { cursor, limit, search } - cursor-based pagination
export const getMyChatsByUser = async (userId, params = {}) => {
  try {
    console.log('🔍 Fetching My chats for user ID:', userId);
    const response = await apiClient.get('/api/chats/MybyUser/', { params: { id: userId, ...params } });
    console.log('📡 MybyUser API Response:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error fetching My chats by user:', error);
    throw error;
  }
};

// Get All chats by user ID for filtering (placeholder for future implementation)
export const getAllChatsByUser = async (userId) => {
  try {
    console.log('🔍 Fetching All chats for user ID:', userId);
    // TODO: Implement the actual API endpoint
    // const response = await apiClient.get(`/api/chats/AllbyUser/?id=${userId}`);
    // return response.data;
    
    // Placeholder response for now
    return {
      isSuccess: false,
      data: [],
      error: 'AllbyUser API not implemented yet'
    };
  } catch (error) {
    console.error('Error fetching All chats by user:', error);
    throw error;
  }
};

// Get Inbox chats by user ID for filtering
// params: { cursor, limit, search } - cursor-based pagination
export const getInboxChatsByUser = async (userId, params = {}) => {
  try {
    console.log('🔍 Fetching Inbox chats for user ID:', userId);
    const response = await apiClient.get('/api/chats/InboxbyUser/', { params: { id: userId, ...params } });
    console.log('📡 InboxbyUser API Response:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error fetching Inbox chats by user:', error);
    throw error;
  }
};

// Get Alerts chats by user ID for filtering (placeholder for future implementation)
export const getAlertsChatsByUser = async (userId) => {
  try {
    console.log('🔍 Fetching Alerts chats for user ID:', userId);
    // TODO: Implement the actual API endpoint
    // const response = await apiClient.get(`/api/chats/AlertsbyUser/?id=${userId}`);
    // return response.data;
    
    // Placeholder response for now
    return {
      isSuccess: false,
      data: [],
      error: 'AlertsbyUser API not implemented yet'
    };
  } catch (error) {
    console.error('Error fetching Alerts chats by user:', error);
    throw error;
  }
};

// Add comment to a conversation
export const addConversationComment = async (conversationId, commentData) => {
  try {
    console.log('💬 Adding comment to conversation:', conversationId);
    console.log('📝 Comment data:', commentData);
    
    const response = await apiClient.post(
      `/api/chats/conversations/${conversationId}/comments/`,
      {
        body: commentData.body
      },
      {
        headers: {
          'Content-Type': 'application/json'
        }
      }
    );
    
    console.log('✅ Comment added successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Error adding comment:', error);
    if (error.response) {
      console.error('❌ Response error details:', {
        status: error.response.status,
        statusText: error.response.statusText,
        data: error.response.data
      });
    }
    throw error;
  }
};

// Get comments for a conversation
export const getConversationComments = async (conversationId) => {
  try {
    console.log('💬 Fetching comments for conversation:', conversationId);
    
    const response = await apiClient.get(
      `/api/chats/conversations/${conversationId}/comments/`
    );
    
    console.log('✅ Comments fetched successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Error fetching comments:', error);
    if (error.response) {
      console.error('❌ Response error details:', {
        status: error.response.status,
        statusText: error.response.statusText,
        data: error.response.data
      });
    }
    throw error;
  }
};

// Get the customer satisfaction feedback summary for a conversation
export const getConversationFeedbackSummary = async (conversationId) => {
  try {
    const response = await apiClient.get(
      `/api/chats/conversations/${conversationId}/feedback-summary/`
    );
    return response.data;
  } catch (error) {
    console.error('❌ Error fetching feedback summary:', error);
    throw error;
  }
};

// Update a comment (PATCH method)
export const updateConversationComment = async (conversationId, commentId, commentData) => {
  try {
    console.log('📝 Updating comment:', commentId);
    console.log('📝 Comment data:', commentData);
    
    const response = await apiClient.patch(
      `/api/chats/conversations/${conversationId}/comments/${commentId}/`,
      {
        body: commentData.body
      },
      {
        headers: {
          'Content-Type': 'application/json'
        }
      }
    );
    
    console.log('✅ Comment updated successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Error updating comment:', error);
    if (error.response) {
      console.error('❌ Response error details:', {
        status: error.response.status,
        statusText: error.response.statusText,
        data: error.response.data
      });
    }
    throw error;
  }
};

// Delete a comment
export const deleteConversationComment = async (conversationId, commentId) => {
  try {
    console.log('🗑️ Deleting comment:', commentId);
    
    const response = await apiClient.delete(
      `/api/chats/conversations/${conversationId}/comments/${commentId}/`
    );
    
    console.log('✅ Comment deleted successfully');
    return response.data;
  } catch (error) {
    console.error('❌ Error deleting comment:', error);
    if (error.response) {
      console.error('❌ Response error details:', {
        status: error.response.status,
        statusText: error.response.statusText,
        data: error.response.data
      });
    }
    throw error;
  }
};

// Get conversations by contact ID
export const getConversationsByContact = async (contactId) => {
  try {
    const response = await apiClient.get(
      getApiUrl('CHAT', 'GET_CONVERSATIONS_BY_CONTACT'),
      {
        params: {
          contact_id: contactId
        }
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching conversations by contact:', error);
    throw error;
  }
};

// Create FAQ
export const createFAQ = async (faqData) => {
  try {
    console.log('❓ Creating FAQ:', faqData);
    
    const response = await apiClient.post(
      getApiUrl('CHAT', 'CREATE_FAQ'),
      {
        question: faqData.question,
        answer: faqData.answer
      },
      {
        headers: {
          'Content-Type': 'application/json'
        }
      }
    );
    
    console.log('✅ FAQ created successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Error creating FAQ:', error);
    if (error.response) {
      console.error('❌ Response error details:', {
        status: error.response.status,
        statusText: error.response.statusText,
        data: error.response.data
      });
    }
    throw error;
  }
};
